import { useEffect, useState, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import axios from 'axios'
import { t } from 'i18next'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import { Button, Card, CardBody, CardFooter } from 'reactstrap'
import Swal from 'sweetalert2'
import moment from 'moment'

import env from '../../env/src_config'
import { headersState, tokenState, multiCheckLoading } from '../../recoil/recoil'
import { axiosError } from '../../helpers/response'
import { isNull, displayDate } from '../../izUtils'
import { transformer, linker } from '../../helpers/fields'
import Spinner from '../spinner/Spinner'
import { errorStatus } from '../../helpers/response'
import ClientInput from '../form/custom/ClientInput'
import { getSingleItem } from '../../helpers/getSingleItem'
import DisplayFields from '../../helpers/displayFields'
import DictionaryInfoButton from '../dictionary/DictionaryInfoButton'

const AddUpdateUser = ({ getUsers, usersConfirmed, users, filters }) => {
    const Navigate = useNavigate();
    const { userId } = useParams()

    const headers = useRecoilValue(headersState);
    const tokenData = useRecoilValue(tokenState);
    const setMultiCheckLoading = useSetRecoilState(multiCheckLoading);
    const [inputs, setInputs] = useState(null)
    const [userRequests, setUserRequests] = useState(null)
    const [rejected, setRejected] = useState(null)
    const [showErrors, setShowErrors] = useState(false)

    useEffect(() => {
        if (!isNull(inputs)) setInputs(null)
        setShowErrors(false);

        // Admin can edit users
        if (tokenData.parsedToken.type === 'admin') {
            updateData(false, null);
        } else {
            getSingleItem(headers, userId, '/api/user/', Navigate).then(data => {
                if (!isNull(data)) {
                    if (data.message === 'Dostop zavrnjen') {
                        Navigate('/')
                    } else  {
                        setInputs(data.fields);
                    }
                }
            })
        }

    }, [userId]) // eslint-disable-line react-hooks/exhaustive-deps

    // Get user requests
    useEffect(() => {
        if (tokenData.parsedToken.type === 'admin') {
            let theUser = users.find(user => user.id === userId);
            if (!isNull(theUser) && theUser.list_requests) {
                getUserRequests(false, null);
            }
        }

    }, [userId, users]) // eslint-disable-line

    const updateData = (save, passedInputs) => {
        let payload = { save };
        if (!isNull(passedInputs)) {
            // Get data from inputs
            const keys = Object.keys(passedInputs);
            payload.data = {};
            for (let i = 0; i < keys.length; i++) {
                if (!isNull(passedInputs[keys[i]].value) && passedInputs[keys[i]].value.length !== 0) {
                    payload.data[keys[i]] = passedInputs[keys[i]].value;
                } else {
                    payload.data[keys[i]] = "";
                }
            }
        }

        let method = "patch";
        if (userId === 'create') method = "post";
        axios[method](env.api + '/api/user/' + userId, payload, {headers}).then(response => {
            if (save) {
                if (!isNull(response.data.state) && response.data.state === 'success') {
                    let title = t('users.update.success.title');
                    let text = t('users.update.success.text');
                    if (userId === 'create') {
                        title = t('users.create.success.title');
                        text = t('users.create.success.text');
                    }

                     if (filters.tabs.value === 'requests') {
                        title = t('users.requests.success.title');
                        text = t('users.requests.success.text');
                    } else if (filters.tabs.value === 'rejected') {
                        title = t('users.rejected.success.title');
                        text = t('users.rejected.success.text');
                    }

                    getUsers(filters);

                    Swal.fire({
                        title, text,
                        icon: 'success',
                        confirmButtonColor: 'var(--theme-default)',
                        confirmButtonText: t('ok'),
                    })
                } else {
                    setShowErrors(true)
                    errorStatus(response.data, t);
                }
            }

            if (!isNull(response.data.data)) setInputs(transformer(response.data.data));
            setRejected(response.data.rejected);
            setMultiCheckLoading(false) // Set loading for field MultiCheck to false
        }).catch(error => {
            axiosError(error, Navigate);
        });
    }

    const getUserRequests = () => {
        let payload = {};
        axios.post(env.api + '/api/user/request/' + userId, payload, {headers}).then(response => {
            setUserRequests(response.data.data);
        }).catch(error => {
            axiosError(error, Navigate);
        });
    }

    const rejectRestoreUser = (type) => {
        axios.get(env.api + '/api/user/' + type + '/' + userId, {headers}).then(response => {
            if (response.data.state === 'success') getUsers(filters);

            Swal.fire({
                text: response.data.message,
                icon: response.data.state === 'success' ? 'success' : 'error',
                confirmButtonColor: 'var(--theme-default)',
                confirmButtonText: t('ok'),
            })
        }).catch(error => {
            axiosError(error, Navigate);
        });
    }

    const textChange = (value, name) => {
        let clonedInputs = {...inputs}

        if (name === 'name') {
            value = value.replace(/^\s+|\s+$/g, '').replace(/\s+/g, ' '); // Remove leading, trailing and multiple spaces
        }

        clonedInputs[name].value = value;
        updateData(false, clonedInputs);
    }

    const multiChange = (value, name, component) => {
        setMultiCheckLoading(name + '-' + component?.id)
        let clonedInputs = {...inputs};
        if (clonedInputs[name].value.length === 0) {
            clonedInputs[name].value.push(value)
        } else {
            if (clonedInputs[name].value.indexOf(value) === -1) {
                clonedInputs[name].value.push(value);
            } else {
                const filteredValues = clonedInputs[name].value.filter(el => el !== value);
                clonedInputs[name].value = filteredValues;
            }
        }
        updateData(false, clonedInputs);
    }

    // Create fields
    const mapLinker = (field) => {
        const inputlinkerFields = {
            field,
            inputs,
            showErrors,
            showDictionary: {
                form: 'users',
                fields: ['name', 'phone', 'registration_notes', 'password', 'active', 'demanding_work', 'department'],
            },
            component: {id: 'addUser'},
            textHandler: (value, id) => textChange(value, id),
            selectHandler: (value, id) => textChange(value, id),
            booleanHandler: (value, id) => textChange(value, id),
            multiHandler: (value, id, component) => multiChange(value, id, component),
        }

        if (tokenData.parsedToken.type === 'admin') {
            return linker(inputlinkerFields);
        } else {
            if (!isNull(inputs[field])) return <DisplayFields key={'display-'+inputs[field].name} data={inputs[field]} />
        }
    }

    let ribbonTitle = useMemo(() => {
        if (filters.tabs.value === 'users') {
            if (userId === 'create') {
                return t('users.create.title');
            } else {
                return t('users.update.title');
            }
        } else if (filters.tabs.value === 'requests') {
            return t('users.requests.title');
        } else {
            return t('users.rejected.title');
        }
    }, [userId, filters])

    return (
        <Card className="ribbon-wrapper">
            <CardBody>
                <div className="ribbon ribbon-clip ribbon-primary">
                    <div className="d-flex align-items-center">
                        {ribbonTitle}
                        {((filters.tabs.value === 'users') && (userId !== 'create')) && <DictionaryInfoButton code="users.form.title_users" id="users.form.title_users" />}
                        {((filters.tabs.value === 'requests') && (userId !== 'create')) && <DictionaryInfoButton code="users.form.title_requests" id="users.form.title_requests" />}
                    </div>
                </div>
                {isNull(inputs) ?
                    <Spinner />
                    :
                    <>
                        <div className="theme-form">
                            { ['email', 'name', 'type', 'phone', 'registration_notes', 'password', 'password_confirmation', 'active', 'signature', 'demanding_work', 'department'].map(field => mapLinker(field)) }
                            {!isNull(inputs['client']) && <ClientInput data={inputs['client']} onBlur={textChange} showErrors={showErrors} />}
                        </div>
                        {(userId !== 'create' && inputs.type?.value !== 'accountant' && inputs.type?.value !== 'client') &&
                            <Button color="secondary" className="my-2" onClick={() => Navigate("/users/" + userId + '/location')}>{t('users.location.check')}</Button>
                        }
                    </>
                }
                {(!isNull(userRequests) && userRequests.length !== 0) &&
                    <div className='mt-5'>
                        <h5>{t('users.create.requests.title')}</h5>
                        <div className='user-facility-request mb-3'>
                            {userRequests.map((request, index) => (
                                <div key={"user-request-"+index} style={ isNull(request.viewed_at) ? {color: 'red'} : {} }>
                                    {index !== 0 && <hr/>}
                                    {!isNull(request.created_at) && <div className='text-underline'><b>{displayDate(moment.utc(request.created_at), null, true)}</b></div>}
                                    <div className='mb-2'>{request.message}</div>
                                    {!isNull(request.viewed_at) && <div><small>{t('read')}: {displayDate(moment.utc(request.viewed_at), null, true)}</small></div>}
                                </div>
                            ))}
                        </div>
                    </div>
                }
            </CardBody>
            {(tokenData.parsedToken.type === 'admin') &&
                <CardFooter>
                    <div className='text-end'>
                        {(!isNull(inputs) && inputs.type.value === 'client') && <Button color="primary" className="mt-2 me-2" onClick={() => rejectRestoreUser(rejected ? 'restore' : 'reject')}>{rejected ? t('restore') : t('reject')}</Button>}
                        <div className="d-flex align-items-center mt-2">
                            <Button color="primary" className="me-2" onClick={() => updateData(true, inputs)}>{(userId === 'create') ? t('add') : t('save')}</Button>
                            {(userId === 'create') && <DictionaryInfoButton code="users.form.add" id="users.form.add" />}
                        </div>
                    </div>
                </CardFooter>
            }
        </Card>
    )
}

export default AddUpdateUser